/** Operations → Safety meeting: most-missed quiz questions + expiring safety tickets (WHMIS, Working at Heights…). */
import { useEffect, useState } from "react";
import { HelpCircle, BadgeAlert, AlertTriangle, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function QuizStats() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/safety/quiz/stats", { params: { days: 90 } }).then(({ data }) => setData(data)).catch(() => setData({ questions: [], answers: 0 })); }, []);
  if (!data || !data.answers) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs" data-testid="safety-quiz-stats">
      <div className="flex items-center gap-2 text-sm font-bold text-white"><HelpCircle className="h-4 w-4 text-[#1a3fd1]" /> Most missed quiz questions <span className="text-[11px] font-normal text-white/50">· last 90 days · {data.answers} answers</span></div>
      <p className="mt-0.5 text-white/60">Re-teach the ones at the top at your next toolbox talk.</p>
      <div className="mt-2 space-y-1.5">
        {data.questions.slice(0, 6).map((q, i) => (
          <div key={q.question_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 px-2.5 py-1.5" data-testid={`safety-quiz-stat-${i}`}>
            <div className="mr-auto min-w-0 flex-1">
              <div className={`truncate font-semibold ${q.deleted ? "text-white/40 line-through" : "text-white"}`}>{q.text}</div>
              {q.top_wrong_answer && <div className="text-[11px] text-white/55">Most common wrong answer: <span className="text-amber-300">{q.top_wrong_answer}</span>{q.correct_answer ? <> · correct: <span className="text-emerald-300">{q.correct_answer}</span></> : null}</div>}
            </div>
            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-white/10"><div className={`h-full ${q.miss_rate >= 0.5 ? "bg-red-400" : q.miss_rate >= 0.25 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.round(q.miss_rate * 100)}%` }} /></div>
            <span className={`w-36 whitespace-nowrap text-right font-mono ${q.miss_rate >= 0.5 ? "text-red-300" : q.miss_rate >= 0.25 ? "text-amber-300" : "text-emerald-300"}`} data-testid={`safety-quiz-stat-${i}-rate`}>missed {q.missed}/{q.asked} · {Math.round(q.miss_rate * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CertReminders() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/safety/certificates/expiring", { params: { days: 30 } }).then(({ data }) => setItems(data.items || [])).catch(() => setItems([])); }, []);
  const nudge = async () => {
    setBusy(true);
    try { const { data } = await api.post("/safety/certificates/nudge"); toast.success(data.nudged ? `Reminders sent for ${data.certificates.length} ticket${data.certificates.length === 1 ? "" : "s"}` : "Nothing new to remind — every ticket already had its 30 / 7-day nudge"); }
    catch { toast.error("Could not send reminders"); }
    finally { setBusy(false); }
  };
  if (!items) return null;
  const expired = items.filter((i) => i.expired), soon = items.filter((i) => !i.expired);
  return (
    <div className={`rounded-xl border p-3 text-xs ${expired.length ? "border-red-400/40 bg-red-500/10" : soon.length ? "border-amber-400/30 bg-amber-500/10" : "border-white/10 bg-white/[0.03]"}`} data-testid="safety-cert-reminders">
      <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
        {expired.length ? <AlertTriangle className="h-4 w-4 text-red-300" /> : <BadgeAlert className="h-4 w-4 text-amber-300" />}
        Safety tickets expiring within 30 days <span className="text-[11px] font-normal text-white/50">({items.length})</span>
        <span className="ml-auto text-[11px] font-normal text-white/50">Workers + office get a chat reminder 30 and 7 days out, and when a ticket lapses (daily 7:30am)</span>
        {items.length > 0 && <button onClick={nudge} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[11px] font-semibold text-white/80 disabled:opacity-50" data-testid="safety-cert-nudge-now">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />} Remind now</button>}
      </div>
      {!items.length && <p className="mt-0.5 text-white/60" data-testid="safety-cert-none">No WHMIS, Working at Heights or other certificates expire in the next 30 days.</p>}
      {items.length > 0 && (
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.cert_id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-2.5 py-1.5" data-testid={`safety-cert-${c.cert_id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-white">{c.employee_name}</div>
                <div className="truncate text-white/60">{c.name}</div>
              </div>
              <span className={`whitespace-nowrap rounded-full px-2 py-0.5 font-semibold ${c.expired ? "bg-red-500/25 text-red-200" : c.days_left <= 7 ? "bg-amber-500/25 text-amber-200" : "bg-white/10 text-white/80"}`}>
                {c.expired ? `expired ${-c.days_left}d ago` : c.days_left === 0 ? "expires today" : `${c.days_left}d left`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
